import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, Paper, Typography, useTheme } from '@mui/material';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import axiosInstance from 'shared/api/axiosInstance';
import { endpoints } from 'shared/api/endPoints';
import Header from 'shared/components/Header';
import ClockLoader from 'shared/components/ClockLoader';
import useGroupService from 'shared/api/services/groupService';
import usePersistedColumnVisibility from 'shared/hooks/usePersistedColumnVisibility';
import { tokens } from 'shared/theme';
import { getRegistryTenantIdFromSession } from 'shared/utils/sessionProfileStore';
import AssignUsersIntoGroup from './AssignUsersIntoGroup';
import { buildGridToolbarExportProps } from 'shared/utils/tableExportFileName';

const resolveRegistryTenantId = () => {
  const fromSession = getRegistryTenantIdFromSession();
  if (fromSession) return fromSession;
  const env = process.env.REACT_APP_TENANT_ID;
  if (env != null && String(env).trim() !== '') return String(env).trim();
  return null;
};

const unwrapRecords = (axiosResponse) => {
  const records = axiosResponse?.data?.data;
  if (!records || typeof records !== 'object') {
    return { rows: [] };
  }
  return {
    rows: Array.isArray(records.data) ? records.data : [],
  };
};

const UsersAssignedToGroup = () => {
  const navigate = useNavigate();
  const { groupId } = useParams();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { columnVisibilityModel, onColumnVisibilityModelChange } =
    usePersistedColumnVisibility('platform.group-members');
  const { getGroup, getGroupPermissionIds, getGroupMembers, addUserToGroup } = useGroupService();

  const [group, setGroup] = useState(null);
  const [permissionIds, setPermissionIds] = useState([]);
  const [allPermissions, setAllPermissions] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);

  const load = useCallback(async () => {
    if (!groupId) return;
    setLoading(true);
    try {
      const [g, ids, permRes, userRows] = await Promise.all([
        getGroup(groupId),
        getGroupPermissionIds(groupId),
        axiosInstance.get(`${endpoints.permissions.list}?page=0&size=500`),
        getGroupMembers(groupId),
      ]);
      setGroup(g ?? null);
      setPermissionIds(ids.map(String));
      const { rows } = unwrapRecords(permRes);
      setAllPermissions(rows);
      setMembers(userRows);
    } catch (error) {
      const msg =
        error.response?.data?.message ||
        error.message ||
        'Failed to load group users';
      toast.error(typeof msg === 'string' ? msg : 'Failed to load');
      navigate('/groups');
    } finally {
      setLoading(false);
    }
  }, [groupId, getGroup, getGroupPermissionIds, getGroupMembers, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const idSet = useMemo(() => new Set(permissionIds), [permissionIds]);

  const assignedPermissions = useMemo(() => {
    const matched = allPermissions.filter((p) => idSet.has(String(p.id)));
    return [...matched].sort((a, b) =>
      String(a.permission ?? '').localeCompare(String(b.permission ?? ''))
    );
  }, [allPermissions, idSet]);

  const memberRows = useMemo(
    () =>
      members.map((m) => ({
        id: String(m.userId ?? m.id ?? ''),
        userId: String(m.userId ?? m.id ?? ''),
        name: m.name ?? '—',
        email: m.email ?? '—',
        status: m.status ?? '—',
      })),
    [members]
  );

  const columns = useMemo(
    () => [
      { field: 'name', headerName: 'Name', flex: 1, minWidth: 160 },
      { field: 'email', headerName: 'Email', flex: 1.2, minWidth: 200 },
      { field: 'status', headerName: 'Status', width: 120 },
      { field: 'userId', headerName: 'User ID', flex: 1, minWidth: 220 },
    ],
    []
  );

  const groupName = group?.name ?? 'Group';
  const serviceLabel =
    group?.serviceName && group?.serviceCode
      ? `${group.serviceName} (${group.serviceCode})`
      : group?.serviceName || group?.serviceCode || '—';
  const serviceLimitLabel =
    group?.serviceNumberOfUsers == null ? 'Unlimited' : String(group.serviceNumberOfUsers);
  const assignedUsersCount = Number(group?.assignedUsersCount ?? memberRows.length ?? 0);
  const remainingSlots =
    group?.remainingSlots == null ? null : Number(group.remainingSlots);

  const memberUserIds = useMemo(
    () => members.map((m) => String(m.userId ?? m.id ?? '')),
    [members]
  );

  const handleAssignUsers = useCallback(
    async (userIds) => {
      let added = 0;
      let already = 0;
      const failures = [];
      for (const userId of userIds) {
        try {
          const { alreadyMember } = await addUserToGroup(userId, groupId);
          if (alreadyMember) already += 1;
          else added += 1;
        } catch (error) {
          const msg =
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message ||
            'Failed to assign user';
          failures.push(typeof msg === 'string' ? msg : 'Failed to assign user');
        }
      }
      if (added > 0) {
        toast.success(
          added === 1
            ? '1 user assigned to the group'
            : `${added} users assigned to the group`
        );
        await load();
      }
      if (already > 0) {
        toast.error(
          already === 1
            ? '1 user was already in this group'
            : `${already} users were already in this group`
        );
      }
      if (failures.length > 0) {
        toast.error(failures[0]);
      }
      return failures.length === 0 || added > 0;
    },
    [addUserToGroup, groupId, load]
  );

  return (
    <Box m="20px">
      <Header
        title="Users assigned to group"
        subtitle={
          loading ? 'Loading…' : `Group “${groupName}” — members and permissions`
        }
      />
      <Box mt={2} mb={2} display="flex" gap={1} flexWrap="wrap" alignItems="center">
        <Button
          variant="contained"
          startIcon={<PersonAddAlt1Icon />}
          onClick={() => setAssignDialogOpen(true)}
          disabled={!groupId || loading}
          sx={{ textTransform: 'none' }}
        >
          Assign users into group
        </Button>
      </Box>

      <AssignUsersIntoGroup
        open={assignDialogOpen}
        onClose={() => setAssignDialogOpen(false)}
        groupId={groupId}
        registryTenantId={resolveRegistryTenantId()}
        memberUserIds={memberUserIds}
        onAssignUsers={handleAssignUsers}
      />

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <ClockLoader size={48} />
        </Box>
      ) : (
        <>
          <Paper
            elevation={0}
            variant="outlined"
            sx={{
              p: 2,
              maxWidth: 1200,
              mb: 2,
              ...(theme.palette.mode === 'dark' && {
                bgcolor: '#2a2d64',
                borderColor: 'rgba(255,255,255,0.12)',
              }),
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                gap: { xs: 2, md: 3 },
                alignItems: 'flex-start',
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Group details
                </Typography>
                <Typography variant="body2">
                  <strong>Name:</strong> {group?.name ?? '—'}
                </Typography>
                <Typography variant="body2">
                  <strong>Service:</strong> {serviceLabel}
                </Typography>
                <Typography variant="body2">
                  <strong>Service user limit:</strong> {serviceLimitLabel}
                </Typography>
                <Typography variant="body2">
                  <strong>Users already using this service:</strong> {assignedUsersCount}
                </Typography>
                <Typography variant="body2">
                  <strong>Remaining slots:</strong> {remainingSlots == null ? 'Unlimited' : remainingSlots}
                </Typography>
                <Typography variant="body2">
                  <strong>Description:</strong> {group?.description ?? '—'}
                </Typography>
                <Typography variant="body2">
                  <strong>Status:</strong> {group?.status ?? '—'}
                </Typography>
              </Box>

              <Box
                sx={{
                  flex: 1,
                  minWidth: 0,
                  pl: { md: 2 },
                  ml: { md: 0 },
                  borderLeft: { md: '1px solid' },
                  borderTop: { xs: '1px solid', md: 'none' },
                  borderColor: 'divider',
                  pt: { xs: 2, md: 0 },
                }}
              >
                <Typography variant="subtitle1" gutterBottom>
                  Permissions in this group ({assignedPermissions.length})
                </Typography>
                {assignedPermissions.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No permissions assigned. Use Group permissions to assign some.
                  </Typography>
                ) : (
                  <Box component="ul" sx={{ m: 0, p: 0, listStyle: 'none' }}>
                    {assignedPermissions.map((p) => (
                      <Box
                        key={String(p.id)}
                        component="li"
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          gap: 2,
                          py: 1.25,
                          borderBottom: 1,
                          borderColor: 'divider',
                          '&:last-of-type': { borderBottom: 0 },
                        }}
                      >
                        <Box sx={{ minWidth: 0, pr: 1 }}>
                          <Typography variant="body2" component="span">
                            {p.permission ?? '—'}
                          </Typography>
                          {p.description ? (
                            <Typography
                              variant="caption"
                              display="block"
                              color="text.secondary"
                              sx={{ mt: 0.25 }}
                            >
                              {p.description}
                            </Typography>
                          ) : null}
                        </Box>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ flexShrink: 0, textAlign: 'right' }}
                        >
                          {p.permission ?? p.id}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            </Box>
          </Paper>

          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            Users in this group ({memberRows.length})
          </Typography>
          <Box
            height="min(52vh, 480px)"
            sx={{
              '& .MuiDataGrid-toolbarContainer .MuiButton-text': {
                color: `${colors.grey[100]} !important`,
              },
            }}
          >
            <DataGrid
              disableRowSelectionOnClick
              rows={memberRows}
              columns={columns}
              columnVisibilityModel={columnVisibilityModel}
              onColumnVisibilityModelChange={onColumnVisibilityModelChange}
              slots={{ toolbar: GridToolbar }}
              slotProps={{
                toolbar: buildGridToolbarExportProps({
                  title: groupName
                    ? `Users assigned to group ${groupName}`
                    : 'Users assigned to group',
                  tableId: 'platform.group-users',
                  showQuickFilter: true,
                  quickFilterProps: { debounceMs: 500 },
                }),
              }}
            />
          </Box>
        </>
      )}
    </Box>
  );
};

export default UsersAssignedToGroup;
